import {
    faCalendarCheck,
    faCog,
    faHeadset,
    faInfoCircle,
    faPiggyBank,
    faPlaneDeparture,
    faRoad,
    faTaxi,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Head } from '@inertiajs/react';
import { isDarkMode } from '@onwardtravelsolutions/package-react/Utilities/browser';
import { Card, Layout, Quote, Rule, Section } from '../Components';
import { Banner, BannerHeader, BannerText } from '../Components/Banners';
import { NoticeErrorBoundary } from '../Components/ErrorBoundaries';
import { SignUpForm } from '../Components/Forms';
import { SplitBlock, SplitBlockHeader } from '../Components/SplitBlocks';
import { ReCaptcha } from '../Types';
import { TrustpilotReviews, TrustpilotScore } from '../Components/Trustpilot';

const benefits = [
    {
        title: "It's Free",
        icon: faPiggyBank,
        content:
            'Zero cost to you; there are no rent or membership fees for working with us. The price you quote is the price you receive.',
    },
    {
        title: '24/7 Assistance',
        icon: faHeadset,
        content:
            'We are on-hand at all times of day to resolve any issues that might arise before, during or after your journey.',
    },
    {
        title: 'Save Miles With Tie-Ins',
        icon: faRoad,
        content:
            'We continuously look for suitable Tie-Ins in your area to save you dead mileage.',
    },
    {
        title: 'Supplier Portal',
        icon: faCog,
        content:
            'Our bespoke Supplier Portal is designed with drivers in mind; fully functional on smaller-screened portable devices and larger desktops/laptops alike.',
    },
    {
        title: 'High Value Airport Travel',
        icon: faPlaneDeparture,
        content:
            'The vast majority of our work is high value airport travel; meaning clean, low-fuss journeys for a mixture of business and holiday travellers.',
    },
    {
        title: 'Automatic Weekly Payment',
        icon: faCalendarCheck,
        content:
            'We understand the importance of prompt & easy payment, just enable "Auto Billing" on your Supplier Portal account and we will automatically pay you weekly for all completed work.',
    },
    {
        title: 'Flexibility',
        icon: faTaxi,
        content:
            "Quote as often as you like. No quotas to fill or commitment before accepting a job. You choose which work to take on and how busy you'd like to be.",
    },
    {
        title: 'In-house Software Team',
        icon: faInfoCircle,
        content:
            'Our in-house development team are continually working on new Supplier Portal features and tools to make your life easier.',
    },
];

const links = [
    {
        href: '/drivers#why-ots',
        title: 'Why work with Onward Travel Solutions?',
        label: 'Why OTS?',
    },
    {
        href: '/drivers#about',
        title: 'About Onward Travel Solutions',
        label: 'About',
    },
    {
        href: '/drivers#sign-up',
        title: 'Sign-Up with Onward Travel Solutions',
        label: 'Sign-Up',
    },
];

type DriversProps = {
    recaptcha: ReCaptcha;
};

const Drivers = ({ recaptcha }: DriversProps) => {
    return (
        <>
            <Head>
                <title>Drivers</title>
                <meta
                    name='description'
                    content='Find out more about becoming an OTS Driver and register now to start filling those dead miles, with zero cost to you'
                />
            </Head>

            <Banner className='py-38'>
                <BannerHeader>Drivers</BannerHeader>
                <Rule className='mx-auto w-1/2 max-w-60' />
                <BannerText>
                    Don't return empty from the Airport; Sign up with OTS today
                    and start filling those dead miles, with zero cost to you.
                </BannerText>
                <TrustpilotScore />
            </Banner>

            <Section className='py-20 lg:py-38' id='why-ots'>
                <div className='m-auto [&>*]:mb-5 text-base md:text-lg lg:text-xl prose-h2:text-3xl prose-h2:lg:text-4xl text-center prose-headings:uppercase prose'>
                    <h2>Why Sign Up?</h2>
                    <Rule className='m-auto w-1/2 max-w-60' />
                    <p>
                        We offer pre-booked transfers to and from all major UK
                        airports for various vehicle types: Saloon, Estate Car,
                        People Carrier, Executive Car, Executive People Carrier
                        and 8 Seater Minibuses.
                    </p>
                </div>
                <div className='gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-[3em]'>
                    {benefits.map((benefit, i) => (
                        <Card
                            key={`benefit-${i}`}
                            id={`benefit-${i}`}
                            className='bg-white dark:bg-neutral-900 py-10 dark:border dark:border-neutral-500 dark:border-solid text-center'
                        >
                            <div className='space-y-4'>
                                <FontAwesomeIcon
                                    icon={benefit.icon}
                                    className='text-neutral-400'
                                    fontSize={32}
                                />
                                <h3 className='font-semibold text-secondary text-xl'>
                                    {benefit.title}
                                </h3>
                            </div>
                            <p className='font-light text-slate-700 dark:text-gray-300'>
                                {benefit.content}
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            <SplitBlock
                id='about'
                path='private-hire-jobs-call-centre'
                alt='Onward Travel Solutions Ltd Vision'
                className='[&>div]:md:flex-row-reverse bg-white dark:bg-neutral-900'
            >
                <Quote author='Nigel Beer, Managing Director'>
                    <p>
                        Since launching Onward Travel Solutions in 2006, our
                        mission has always been to provide UK airport transfers
                        and exceptional service at affordable prices.
                    </p>
                    <p>
                        Having been a taxi driver for many years, I developed an
                        extensive understanding of the industry and recognised
                        an opportunity to move it forward.
                    </p>
                    <p>
                        By investing in technology and developing systems that
                        efficiently connect travellers and transfer companies,
                        we've been able to reduce the overheads taxi drivers and
                        companies normally face. We handle the sourcing,
                        marketing and customer communication for our associated
                        transfer companies, allowing them to focus on simply
                        providing a great service to the customer on their day
                        of travel.
                    </p>
                    <p>
                        Our systems are now sophisticated enough to detect and
                        notify our associates of tie-in journeys that can
                        potentially save 'dead' miles; a win for the environment
                        and you the supplier.
                    </p>
                </Quote>
            </SplitBlock>

            <Section className='py-20 lg:py-38' id='sign-up'>
                <div className='mx-auto mb-14 [&>*]:mb-5 md:text-lg lg:text-xl prose-h2:text-3xl prose-h2:lg:text-4xl text-center prose-headings:uppercase prose'>
                    <h2>Sign Up Below</h2>
                    <Rule className='mx-auto w-1/2 max-w-60' />
                    <p>
                        Fill in your contact information below for more
                        information on working with OTS and to start our quick
                        and easy sign-up process.
                    </p>
                </div>
                <NoticeErrorBoundary>
                    <SignUpForm recaptcha={recaptcha} />
                </NoticeErrorBoundary>
            </Section>

            <Section
                className='bg-white dark:bg-neutral-900 py-15 lg:py-20'
                id='trustpilot-reviews'
            >
                <TrustpilotReviews theme={isDarkMode() ? 'dark' : 'light'} />
            </Section>

            <SplitBlock
                id='benefits'
                path='private-hire-driver-uk'
                alt='Onward Travel Solutions Ltd Vision'
                className='bg-gray-100 dark:bg-neutral-800'
            >
                <SplitBlockHeader>
                    Driving Your Private Hire Work
                </SplitBlockHeader>
                <p>
                    We make securing high-quality private hire taxi contracts
                    effortless by giving you direct access to our extensive
                    range of airport taxi jobs across the UK. Available 24/7,
                    our platform lets you stay in full control, allowing you to
                    choose the jobs that suit you best.
                </p>
                <p>
                    Whether you're a private hire operator, a hackney carriage
                    driver, or not yet licensed, we're here to help you get
                    started. Simply fill out the sign-up form above and begin
                    your journey with us today.
                </p>
            </SplitBlock>
        </>
    );
};

Drivers.layout = (page: any) => (
    <Layout
        {...{
            env: page.props?.env,
            children: page,
            links: links,
            notices: page.props?.notices,
        }}
    />
);

export default Drivers;
